import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Card,
  CardContent,
  Stack,
  Typography,
} from "@mui/material";
import type { Dispatch, SetStateAction } from "react";
import FiltersPanelThree from "../FiltersPanelThree";
import CommitmentsByDenominationTreemap from "../charts/CommitmentsByDenominationTreemap";
import StackedYearlyCommitmentChart from "../charts/StackedYearlyCommitmentChart";
import type { AnalyticsFilters, YearlyRow } from "../types";

export type ExpandedLoanSections = {
  denomination: boolean;
  instrument: boolean;
  governingLaw: boolean;
  gtc: boolean;
  borrowerRegion: boolean;
};

type LoanCharacteristicsTabProps = {
  expandedLoanSections: ExpandedLoanSections;
  setExpandedLoanSections: Dispatch<SetStateAction<ExpandedLoanSections>>;
  filters: AnalyticsFilters;
  setFilters: Dispatch<SetStateAction<AnalyticsFilters>>;
  creditorRegionOptions: string[];
  borrowerRegionOptions: string[];
  denominationCreditorTypeColumns: string[];
  denominationColumns: string[];
  minYear: number;
  maxYear: number;
  aggregatedDenominationYearRows: YearlyRow[];
  denominationYears: number[];
  selectedDenominationColumnsFromTemporal: string[];
  denominationPalette: string[];
  denominationColorMap: Record<string, string>;
  instrumentTemporalYearRows: YearlyRow[];
  filteredYears: number[];
  selectedInstrumentColumns: string[];
  instrumentPalette: string[];
  governingLawTemporalYearRows: YearlyRow[];
  selectedGoverningLawColumns: string[];
  governingLawPalette: string[];
  gtcTemporalYearRows: YearlyRow[];
  selectedGtcColumns: string[];
  gtcPalette: string[];
};

const LOAN_CHARACTERISTICS_Y_AXIS_TITLE = "Commitment Size in Billions of USD";

function LoanSectionChevron() {
  return (
    <Box
      component="svg"
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 -960 960 960"
      sx={{ width: 24, height: 24, fill: "#ffffff" }}
    >
      <path d="M480-344 240-584l47.33-47.33L480-438.67l192.67-192.66L720-584 480-344Z" />
    </Box>
  );
}

export default function LoanCharacteristicsTab({
  expandedLoanSections,
  setExpandedLoanSections,
  filters,
  setFilters,
  creditorRegionOptions,
  borrowerRegionOptions,
  denominationCreditorTypeColumns,
  denominationColumns,
  minYear,
  maxYear,
  aggregatedDenominationYearRows,
  denominationYears,
  selectedDenominationColumnsFromTemporal,
  denominationPalette,
  denominationColorMap,
  instrumentTemporalYearRows,
  filteredYears,
  selectedInstrumentColumns,
  instrumentPalette,
  governingLawTemporalYearRows,
  selectedGoverningLawColumns,
  governingLawPalette,
  gtcTemporalYearRows,
  selectedGtcColumns,
  gtcPalette,
}: LoanCharacteristicsTabProps) {
  return (
    <Stack spacing={2.5}>
      <Card sx={{ px: "1rem", py: "1rem" }}>
        <CardContent>
          <Typography sx={{ fontSize: "1rem", color: "#374151" }}>
            Sovereign debt must be accountable to the public. Use the
            interactive visualizations to compare characteristics of our
            collected agreements and better understand how this composition
            changes over time.
          </Typography>
        </CardContent>
      </Card>

      <Card sx={{ px: "1rem", py: "2rem" }}>
        <CardContent
          sx={{ display: "flex", flexDirection: "column", gap: "1rem" }}
        >
          <Accordion
            disableGutters
            expanded={expandedLoanSections.denomination}
            onChange={(_, expanded) =>
              setExpandedLoanSections((prev) => ({
                ...prev,
                denomination: expanded,
              }))
            }
            sx={{
              borderRadius: 1.5,
              boxShadow: "none",
              "&::before": { display: "none" },
            }}
          >
            <AccordionSummary
              expandIcon={<LoanSectionChevron />}
              sx={{
                bgcolor: "#12448f",
                color: "#fff",
                borderRadius: 1.5,
                minHeight: 44,
                "& .MuiAccordionSummary-content": { my: 0.7 },
                "& .MuiAccordionSummary-expandIconWrapper": {
                  color: "#fff",
                },
              }}
            >
              <Typography sx={{ fontWeight: 700, fontSize: "0.95rem" }}>
                Characteristic: Denomination
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 2, py: 3 }}>
              <Stack spacing={2} useFlexGap>
                <Typography sx={{ fontSize: "0.95rem", color: "#374151" }}>
                  {/* {LOAN_SECTION_COPY} */}
                  These visuals show the currency denomination of our documents
                  by commitment size and over time. A user can see the current
                  makeup of loans using certain denominations for a given
                  region, and how the size and denomination of loans have
                  changed over time.
                </Typography>
                <FiltersPanelThree
                  filters={filters}
                  setFilters={setFilters}
                  creditorRegionOptions={creditorRegionOptions}
                  borrowerRegionOptions={borrowerRegionOptions}
                  creditorTypeColumns={denominationCreditorTypeColumns}
                  denominationColumns={denominationColumns}
                  minYear={minYear}
                  maxYear={maxYear}
                  horizontal
                  noCard
                />
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" },
                    gap: 2,
                  }}
                >
                  <StackedYearlyCommitmentChart
                    title="Commitment Size by Denomination and Year"
                    rows={aggregatedDenominationYearRows}
                    years={denominationYears}
                    columns={selectedDenominationColumnsFromTemporal}
                    palette={denominationPalette}
                    colorMap={denominationColorMap}
                    showStackTotalInTooltip
                    topGrid={60}
                    yAxisTitle={LOAN_CHARACTERISTICS_Y_AXIS_TITLE}
                  />
                  <CommitmentsByDenominationTreemap
                    rows={aggregatedDenominationYearRows}
                    years={denominationYears}
                    columns={selectedDenominationColumnsFromTemporal}
                    palette={denominationPalette}
                    colorMap={denominationColorMap}
                  />
                </Box>
              </Stack>
            </AccordionDetails>
          </Accordion>

          <Accordion
            disableGutters
            expanded={expandedLoanSections.instrument}
            onChange={(_, expanded) =>
              setExpandedLoanSections((prev) => ({
                ...prev,
                instrument: expanded,
              }))
            }
            sx={{
              borderRadius: 1.5,
              boxShadow: "none",
              "&::before": { display: "none" },
            }}
          >
            <AccordionSummary
              expandIcon={<LoanSectionChevron />}
              sx={{
                bgcolor: "#12448f",
                color: "#fff",
                borderRadius: 1.5,
                minHeight: 44,
                "& .MuiAccordionSummary-content": { my: 0.7 },
                "& .MuiAccordionSummary-expandIconWrapper": {
                  color: "#fff",
                },
              }}
            >
              <Typography sx={{ fontWeight: 700, fontSize: "0.95rem" }}>
                Characteristic: Instrument Type
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 2, py: 3 }}>
              <Stack spacing={2}>
                <Typography sx={{ fontSize: "0.95rem", color: "#374151" }}>
                  These visuals show the type of instruments of our documents by
                  commitment size and over time. A user can see the current
                  makeup of instrument types for a given region, and how the
                  size and instrument types have changed over time.{" "}
                </Typography>
                <FiltersPanelThree
                  filters={filters}
                  setFilters={setFilters}
                  creditorRegionOptions={creditorRegionOptions}
                  borrowerRegionOptions={borrowerRegionOptions}
                  creditorTypeColumns={denominationCreditorTypeColumns}
                  denominationColumns={denominationColumns}
                  minYear={minYear}
                  maxYear={maxYear}
                  horizontal
                  noCard
                />
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" },
                    gap: 2,
                  }}
                >
                  <StackedYearlyCommitmentChart
                    title="Commitment Size by Instrument Type and Year"
                    rows={instrumentTemporalYearRows}
                    years={filteredYears}
                    columns={selectedInstrumentColumns}
                    palette={instrumentPalette}
                    showStackTotalInTooltip
                    topGrid={60}
                    yAxisTitle={LOAN_CHARACTERISTICS_Y_AXIS_TITLE}
                  />
                  <CommitmentsByDenominationTreemap
                    title="Commitments by Instrument Type"
                    rows={instrumentTemporalYearRows}
                    years={filteredYears}
                    columns={selectedInstrumentColumns}
                    palette={instrumentPalette}
                  />
                </Box>
              </Stack>
            </AccordionDetails>
          </Accordion>

          <Accordion
            disableGutters
            expanded={expandedLoanSections.governingLaw}
            onChange={(_, expanded) =>
              setExpandedLoanSections((prev) => ({
                ...prev,
                governingLaw: expanded,
              }))
            }
            sx={{
              borderRadius: 1.5,
              boxShadow: "none",
              "&::before": { display: "none" },
            }}
          >
            <AccordionSummary
              expandIcon={<LoanSectionChevron />}
              sx={{
                bgcolor: "#12448f",
                color: "#fff",
                borderRadius: 1.5,
                minHeight: 44,
                "& .MuiAccordionSummary-content": { my: 0.7 },
                "& .MuiAccordionSummary-expandIconWrapper": {
                  color: "#fff",
                },
              }}
            >
              <Typography sx={{ fontWeight: 700, fontSize: "0.95rem" }}>
                Characteristic: Governing Law
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 2, py: 3 }}>
              <Stack spacing={2}>
                <Typography sx={{ fontSize: "0.95rem", color: "#374151" }}>
                  These visuals show the governing law of our documents by
                  commitment size and over time. A user can see the current
                  makeup of documents using particular governing laws for a
                  given region, and how the size and governing law of documents
                  have changed over time.
                </Typography>
                <FiltersPanelThree
                  filters={filters}
                  setFilters={setFilters}
                  creditorRegionOptions={creditorRegionOptions}
                  borrowerRegionOptions={borrowerRegionOptions}
                  creditorTypeColumns={denominationCreditorTypeColumns}
                  denominationColumns={denominationColumns}
                  minYear={minYear}
                  maxYear={maxYear}
                  horizontal
                  noCard
                />
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" },
                    gap: 2,
                  }}
                >
                  <StackedYearlyCommitmentChart
                    title="Commitment Size by Governing Law and Year"
                    rows={governingLawTemporalYearRows}
                    years={filteredYears}
                    columns={selectedGoverningLawColumns}
                    palette={governingLawPalette}
                    showStackTotalInTooltip
                    topGrid={60}
                    yAxisTitle={LOAN_CHARACTERISTICS_Y_AXIS_TITLE}
                  />
                  <CommitmentsByDenominationTreemap
                    title="Commitments by Governing Law"
                    rows={governingLawTemporalYearRows}
                    years={filteredYears}
                    columns={selectedGoverningLawColumns}
                    palette={governingLawPalette}
                  />
                </Box>
              </Stack>
            </AccordionDetails>
          </Accordion>

          <Accordion
            disableGutters
            expanded={expandedLoanSections.gtc}
            onChange={(_, expanded) =>
              setExpandedLoanSections((prev) => ({
                ...prev,
                gtc: expanded,
              }))
            }
            sx={{
              borderRadius: 1.5,
              boxShadow: "none",
              "&::before": { display: "none" },
            }}
          >
            <AccordionSummary
              expandIcon={<LoanSectionChevron />}
              sx={{
                bgcolor: "#12448f",
                color: "#fff",
                borderRadius: 1.5,
                minHeight: 44,
                "& .MuiAccordionSummary-content": { my: 0.7 },
                "& .MuiAccordionSummary-expandIconWrapper": {
                  color: "#fff",
                },
              }}
            >
              <Typography sx={{ fontWeight: 700, fontSize: "0.95rem" }}>
                Characteristic: General Terms & Conditions
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 2, py: 3 }}>
              <Stack spacing={2}>
                <Typography sx={{ fontSize: "0.95rem", color: "#374151" }}>
                  These visuals show the presence of general terms and
                  conditions linked to our documents by commitment size and over
                  time. A user can see the current makeup of loans using general
                  terms and conditions for a given region, and how the size and
                  use of general terms and conditions have changed over
                  time.{" "}
                </Typography>
                <FiltersPanelThree
                  filters={filters}
                  setFilters={setFilters}
                  creditorRegionOptions={creditorRegionOptions}
                  borrowerRegionOptions={borrowerRegionOptions}
                  creditorTypeColumns={denominationCreditorTypeColumns}
                  denominationColumns={denominationColumns}
                  minYear={minYear}
                  maxYear={maxYear}
                  horizontal
                  noCard
                />
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" },
                    gap: 2,
                  }}
                >
                  <StackedYearlyCommitmentChart
                    title="Commitment Size by GTC and Year"
                    rows={gtcTemporalYearRows}
                    years={filteredYears}
                    columns={selectedGtcColumns}
                    palette={gtcPalette}
                    showStackTotalInTooltip
                    topGrid={60}
                    yAxisTitle={LOAN_CHARACTERISTICS_Y_AXIS_TITLE}
                  />
                  <CommitmentsByDenominationTreemap
                    title="Commitments by GTC"
                    rows={gtcTemporalYearRows}
                    years={filteredYears}
                    columns={selectedGtcColumns}
                    palette={gtcPalette}
                  />
                </Box>
              </Stack>
            </AccordionDetails>
          </Accordion>
        </CardContent>
      </Card>
    </Stack>
  );
}
